import React, { useState, useEffect } from 'react';
import { FileText, Download, DollarSign, Calendar, AlertCircle } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import BillingTable from '../components/BillingTable';
import {
  calculateBillingAmount,
  formatCurrency,
  getFacturableProjects,
  sortByBillingAmount
} from '../utils/billingUtils';

const Facturation = () => {
  const { state } = useApp();
  const [facturableProjects, setFacturableProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'facturees', 'non_facturees'
  const [searchTerm, setSearchTerm] = useState('');

  const loadFacturationData = async () => {
    try {
      setLoading(true);
      
      // Filtrer les projets facturables
      const projets = getFacturableProjects(state.projets?.data || []);
      
      // Enrichir avec les données de temps consommé et de montant à facturer
      const enrichedProjects = projets.map((projet) => {
        // Calculer les jours consommés facturables pour ce projet
        const joursConsommes = (state.tempsConsommes || [])
          .filter(tc => tc.id_projet === projet.id && tc.facturable)
          .reduce((sum, tc) => sum + parseFloat(tc.jours_consommes || 0), 0);

        // Taux journalier par défaut (à améliorer avec données réelles des personnes)
        const tauxJournalier = 350;
        const montantFacturable = calculateBillingAmount(joursConsommes, tauxJournalier);
        
        return {
          ...projet,
          jours_consommes: joursConsommes,
          montant_a_facturer: montantFacturable,
          date_facturation_previsionnelle: projet.date_fin,
          taux: tauxJournalier,
          montant_facture: 0 // À synchroniser avec la base de données
        };
      });

      // Trier par montant à facturer décroissant
      const sorted = sortByBillingAmount(enrichedProjects);
      setFacturableProjects(sorted);
    } catch (error) {
      console.error('Erreur lors du chargement des données de facturation:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Charger les données de facturation
    // eslint-disable-next-line react-hooks/exhaustive-deps
    loadFacturationData();
  }, [state.projets, state.tempsConsommes]);

  const getFilteredProjects = () => {
    let filtered = facturableProjects;

    // Appliquer le filtre de recherche
    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.nom.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Appliquer le filtre d'état de facturation
    if (filter === 'facturees') {
      filtered = filtered.filter(p => p.facture);
    } else if (filter === 'non_facturees') {
      filtered = filtered.filter(p => !p.facture);
    }

    return filtered;
  };


  const totalMontantAFacturer = getFilteredProjects().reduce((sum, p) => sum + p.montant_a_facturer, 0);
  const totalJoursConsommes = getFilteredProjects().reduce((sum, p) => sum + p.jours_consommes, 0);

  const filteredProjects = getFilteredProjects();

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-7xl mx-auto">
      {/* En-tête */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Facturation</h1>
            <p className="text-gray-600 mt-2">Gestion des projets facturables et suivi des montants à facturer</p>
          </div>
          <FileText size={40} className="text-primary-600" />
        </div>
      </div>

      {/* Statistiques globales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Montant total à facturer</p>
              <p className="text-3xl font-bold text-primary-600 mt-2">
                {formatCurrency(totalMontantAFacturer)}
              </p>
              <p className="text-gray-500 text-xs mt-2">{filteredProjects.length} projet(s)</p>
            </div>
            <div className="text-primary-600 opacity-10">
              <DollarSign size={64} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Total jours consommés</p>
              <p className="text-3xl font-bold text-blue-600 mt-2">
                {totalJoursConsommes.toFixed(2)}
              </p>
              <p className="text-gray-500 text-xs mt-2">jours facturables</p>
            </div>
            <div className="text-blue-600 opacity-10">
              <Calendar size={64} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Taux journalier moyen</p>
              <p className="text-3xl font-bold text-green-600 mt-2">
                {totalJoursConsommes > 0 ? formatCurrency(totalMontantAFacturer / totalJoursConsommes) : '—'}
              </p>
              <p className="text-gray-500 text-xs mt-2">par jour</p>
            </div>
            <div className="text-green-600 opacity-10">
              <FileText size={64} />
            </div>
          </div>
        </div>
      </div>

      {/* Filtres et recherche */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Rechercher un projet</label>
            <input
              type="text"
              placeholder="Nom du projet..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Statut de facturation</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">Tous les projets</option>
              <option value="non_facturees">Non facturés</option>
              <option value="facturees">Facturés</option>
            </select>
          </div>
        </div>
      </div>

      {/* Alerte pour projets en retard */}
      {filteredProjects.some(p => new Date(p.date_facturation_previsionnelle) < new Date()) && (
        <div className="mb-8 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-red-900 mb-1">Facturation en retard détectée</h3>
            <p className="text-red-700 text-sm">
              {filteredProjects.filter(p => new Date(p.date_facturation_previsionnelle) < new Date()).length} projet(s) dépassent la date de facturation prévue.
            </p>
          </div>
        </div>
      )}

      {/* Tableau des projets */}
      <BillingTable
        projects={filteredProjects}
        loading={loading}
        onView={(projet) => console.log('View:', projet)}
        onEdit={(projet) => console.log('Edit:', projet)}
        onDelete={(projet) => console.log('Delete:', projet)}
      />

      {/* Résumé bas */}
      {filteredProjects.length > 0 && (
        <div className="mt-6 bg-primary-50 border border-primary-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-primary-900 mb-2">Résumé de la facturation</h3>
              <p className="text-primary-700 text-sm">
                {filteredProjects.length} projet(s) à facturer | 
                {' '} {totalJoursConsommes.toFixed(2)} jours consommés | 
                {' '} <span className="font-bold">{formatCurrency(totalMontantAFacturer)}</span> HT
              </p>
            </div>
            <button className="bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-6 rounded-lg flex items-center gap-2 transition">
              <Download size={18} />
              Générer factures
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Facturation;

